import { Modal, Form } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { User, Shield, Users, Settings, Eye, Edit3, AlertTriangle } from 'lucide-react';

// Phase 1.2 (access-control redesign) note: every `roles` array referenced
// in this modal belongs to the *target* user being edited (passed in via
// props), NOT the caller. There is no caller-self role read to migrate to
// useCapabilities() here. If a future "is the CALLER allowed to perform
// this role-edit?" gate gets added, route it through useCapabilities()
// (isAdmin / isOwner) rather than re-introducing a self-fetch.
const RoleModal = ({ show, handleClose, user, roles, allRoles, handleSaveRoles, associatedUsers }) => {
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (user) {
            setSelectedRoles(user.roles);
            setHasChanges(false);
        }
    }, [user]);

    // Check if removing admin role would leave no admins
    const wouldRemoveLastAdmin = (rolesToUpdate) => {
        const currentAdmins = associatedUsers?.filter(u => u.roles.includes('Admin')) || [];
        const isCurrentUserAdmin = user?.roles.includes('Admin');
        const willBeAdmin = rolesToUpdate.includes('Admin');

        return isCurrentUserAdmin && !willBeAdmin && currentAdmins.length <= 1;
    };

    const handleRoleChange = (e) => {
        const roleName = e.target.name;
        let newRoles;

        if (e.target.checked) {
            newRoles = [...selectedRoles, roleName];
        } else {
            newRoles = selectedRoles.filter((role) => role !== roleName);
        }

        setSelectedRoles(newRoles);
        setHasChanges(JSON.stringify(newRoles.sort()) !== JSON.stringify(user.roles.sort()));
    };

    const handleSubmit = () => {
        if (wouldRemoveLastAdmin(selectedRoles)) {
            return; // Prevent submission
        }
        handleSaveRoles(user.user_id, selectedRoles);
        setHasChanges(false);
    };

    const getRoleIcon = (roleName) => {
        switch (roleName.toLowerCase()) {
            case 'admin':
                return <Shield size={18} className="text-danger" />;
            case 'manager':
                return <Users size={18} className="text-warning" />;
            case 'user':
                return <User size={18} className="text-info" />;
            case 'viewer':
                return <Eye size={18} className="text-secondary" />;
            default:
                return <Settings size={18} className="text-primary" />;
        }
    };

    const getRoleDescription = (roleName) => {
        switch (roleName.toLowerCase()) {
            case 'admin':
                return 'Full åtkomst till alla funktioner och inställningar';
            case 'manager':
                return 'Kan hantera team och projekt, begränsad systemåtkomst';
            case 'user':
                return 'Standard användarrättigheter för dagligt arbete';
            case 'viewer':
                return 'Endast läsrättigheter, kan inte göra ändringar';
            default:
                return 'Anpassad roll med specifika behörigheter';
        }
    };

    const isRoleDisabled = (roleName) => {
        // Disable admin checkbox if it would remove the last admin
        if (roleName === 'Admin' && selectedRoles.includes('Admin')) {
            return wouldRemoveLastAdmin(selectedRoles.filter(r => r !== 'Admin'));
        }
        return false;
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="d-flex align-items-center">
                    <div className="user-avatar-modal me-3">
                        {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                    </div>
                    <div>
                        <Modal.Title className="mb-1 h4">
                            Hantera roller
                        </Modal.Title>
                        <p className="text-muted mb-0 small">
                            {user?.first_name} {user?.last_name} • {user?.email}
                        </p>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                {wouldRemoveLastAdmin(selectedRoles) && (
                    <div className="alert alert-warning d-flex align-items-center mb-4">
                        <AlertTriangle size={20} className="me-2" />
                        <div>
                            <strong>Varning:</strong> Du kan inte ta bort admin-rollen från den sista administratören.
                            Det måste finnas minst en admin i företaget.
                        </div>
                    </div>
                )}

                <div className="mb-3">
                    <h6 className="text-muted mb-3 text-uppercase small fw-bold">
                        Tillgängliga roller
                    </h6>
                </div>

                <Form>
                    <div className="role-list">
                        {allRoles.map((role) => {
                            const isChecked = selectedRoles.includes(role.role_name);
                            const isDisabled = isRoleDisabled(role.role_name);

                            return (
                                <div
                                    key={role.role_id}
                                    className={`role-item ${isChecked ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                >
                                    <div className="role-content">
                                        <div className="d-flex align-items-start">
                                            <div className="role-icon me-3 mt-1">
                                                {getRoleIcon(role.role_name)}
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <h6 className="role-name mb-1">
                                                            {role.role_name}
                                                        </h6>
                                                        <p className="role-description text-muted small mb-0">
                                                            {getRoleDescription(role.role_name)}
                                                        </p>
                                                    </div>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`role-${role.role_name}`}
                                                        name={role.role_name}
                                                        checked={isChecked}
                                                        onChange={handleRoleChange}
                                                        disabled={isDisabled}
                                                        className="role-switch"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Form>

                {selectedRoles.length > 0 && (
                    <div className="mt-4 pt-3 border-top">
                        <h6 className="text-muted mb-2 small">Aktiva roller:</h6>
                        <div className="d-flex flex-wrap gap-2">
                            {selectedRoles.map((roleName, index) => (
                                <span key={index} className="badge bg-primary rounded-pill px-3 py-2">
                                    {getRoleIcon(roleName)}
                                    <span className="ms-2">{roleName}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <Button variant="outline-secondary" onClick={handleClose}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!hasChanges || wouldRemoveLastAdmin(selectedRoles)}
                    className="d-flex align-items-center gap-2"
                >
                    <Edit3 size={16} />
                    Spara ändringar
                </Button>
            </Modal.Footer>

            <style jsx>{`
                .user-avatar-modal {
                    width: 48px;
                    height: 48px;
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 600;
                    font-size: 1rem;
                }

                .role-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .role-item {
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1.25rem;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    background: white;
                }

                .role-item:hover:not(.disabled) {
                    border-color: #3b82f6;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
                    transform: translateY(-1px);
                }

                .role-item.selected {
                    border-color: #3b82f6;
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                }

                .role-item.disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    background: #f8f9fa;
                }

                .role-item.disabled:hover {
                    transform: none;
                    border-color: #e2e8f0;
                    box-shadow: none;
                }

                .role-name {
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.25rem;
                }

                .role-description {
                    font-size: 0.875rem;
                    line-height: 1.4;
                }

                .role-switch .form-check-input {
                    width: 3rem;
                    height: 1.5rem;
                }

                .role-switch .form-check-input:checked {
                    background-color: #3b82f6;
                    border-color: #3b82f6;
                }

                .role-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .badge {
                    display: inline-flex;
                    align-items: center;
                    font-size: 0.875rem;
                    font-weight: 500;
                }

                .modal-header {
                    padding: 1.5rem 1.5rem 0 1.5rem;
                }

                .modal-body {
                    padding: 1.5rem;
                }

                .modal-footer {
                    padding: 0 1.5rem 1.5rem 1.5rem;
                }
            `}</style>
        </Modal>
    );
};

export default RoleModal;


// import { Modal, Form } from 'react-bootstrap';
// import { Button } from 'react-bootstrap';
// import { useState, useEffect } from 'react';


// // ...

// const RoleModal = ({ show, handleClose, user, roles, allRoles, handleSaveRoles }) => {
//     const [selectedRoles, setSelectedRoles] = useState([]);

//     useEffect(() => {
//         if (user) {
//             setSelectedRoles(user.roles);
//         }
//     }, [user]);

//     const handleRoleChange = (e) => {
//         const roleName = e.target.name;
//         if (e.target.checked) {
//             setSelectedRoles([...selectedRoles, roleName]);
//         } else {
//             setSelectedRoles(selectedRoles.filter((role) => role !== roleName));
//         }
//     };

//     const handleSubmit = () => {
//         handleSaveRoles(user.user_id, selectedRoles);
//     };

//     return (
//         <Modal show={show} onHide={handleClose}>
//             <Modal.Header closeButton>
//                 <Modal.Title>Ändra roller för {user?.first_name} {user?.last_name}</Modal.Title>
//             </Modal.Header>
//             <Modal.Body>
//                 <Form>
//                     {allRoles.map((role) => (
//                         <Form.Group controlId={`role-${role.role_name}`} key={role.role_id}>
//                             <Form.Check
//                                 type="checkbox"
//                                 label={role.role_name}
//                                 name={role.role_name}
//                                 checked={selectedRoles.includes(role.role_name)}
//                                 onChange={handleRoleChange}
//                             />
//                         </Form.Group>
//                     ))}
//                 </Form>
//             </Modal.Body>
//             <Modal.Footer>
//                 <Button variant="secondary" onClick={handleClose}>Avbryt</Button>
//                 <Button variant="primary" onClick={handleSubmit}>Spara</Button>
//             </Modal.Footer>
//         </Modal>
//     );
// };

// export default RoleModal;